// Bottom-left zoom pill: − / 100% (reset) / + / ⤢ (fit to content).
// Owns the keyboard shortcuts for zoom (Cmd/Ctrl + = / - / 0 / 1). All math
// flows through pure helpers in viewport.ts so the buttons and the wheel
// handler agree on anchor + clamp semantics.

import { useEffect, useMemo } from 'react';
import { Maximize2, Minus, Plus } from 'lucide-react';
import type { Shape } from '@frame/shared';
import { shapeBBox } from './binding/shapesMap';
import {
  clampScale,
  fitBBoxToContainer,
  MAX_SCALE,
  MIN_SCALE,
  resetZoom,
  unionBBox,
  zoomAt,
  ZOOM_STEP,
  type Size,
  type Viewport,
} from './viewport';

interface Props {
  viewport: Viewport;
  setViewport: (next: Viewport | ((prev: Viewport) => Viewport)) => void;
  containerSize: Size;
  shapes: readonly Shape[];
}

export function ZoomControls({ viewport, setViewport, containerSize, shapes }: Props) {
  const percent = Math.round(viewport.scale * 100);
  const canZoomIn = viewport.scale < MAX_SCALE - 1e-6;
  const canZoomOut = viewport.scale > MIN_SCALE + 1e-6;

  const contentBBox = useMemo(() => {
    const boxes = shapes.map((s) => shapeBBox(s)).filter((b): b is NonNullable<typeof b> => b !== null);
    return unionBBox(boxes);
  }, [shapes]);
  const canFit = contentBBox !== null && containerSize.width > 0 && containerSize.height > 0;

  const center = { x: containerSize.width / 2, y: containerSize.height / 2 };

  const zoomIn = () => setViewport((v) => zoomAt(v, clampScale(v.scale * ZOOM_STEP), center));
  const zoomOut = () => setViewport((v) => zoomAt(v, clampScale(v.scale / ZOOM_STEP), center));
  const reset = () => setViewport((v) => resetZoom(v, containerSize));
  const fit = () => {
    if (!contentBBox) return;
    setViewport(fitBBoxToContainer(contentBBox, containerSize));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        if (contentBBox) {
          setViewport(fitBBoxToContainer(contentBBox, containerSize));
        } else {
          setViewport((v) => resetZoom(v, containerSize));
        }
      } else if (e.key === '1') {
        e.preventDefault();
        setViewport((v) => resetZoom(v, containerSize));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentBBox, containerSize.width, containerSize.height]);

  return (
    <div
      role="group"
      aria-label="Zoom"
      className="
        absolute left-3 bottom-3 z-[3]
        inline-flex items-center
        h-9 px-1 gap-0.5
        bg-bg-panel/95 backdrop-blur
        border border-border-default rounded-2
        shadow-sm
      "
    >
      <PillButton onClick={zoomOut} disabled={!canZoomOut} label="Zoom out" shortcut="⌘−">
        <Minus size={14} strokeWidth={1.75} />
      </PillButton>
      <button
        type="button"
        onClick={reset}
        title="Reset to 100%  ⌘1"
        aria-label="Reset zoom to 100%"
        className="
          font-mono tabular-nums text-12 text-fg-secondary
          w-12 h-7 rounded-1
          inline-flex items-center justify-center
          hover:bg-bg-hover hover:text-fg-primary
          focus-visible:outline-none focus-visible:shadow-focus
          transition-colors duration-micro ease-out
        "
      >
        {percent}%
      </button>
      <PillButton onClick={zoomIn} disabled={!canZoomIn} label="Zoom in" shortcut="⌘+">
        <Plus size={14} strokeWidth={1.75} />
      </PillButton>
      <span aria-hidden className="w-px self-stretch my-1.5 bg-border-subtle mx-0.5" />
      <PillButton onClick={fit} disabled={!canFit} label="Fit to content" shortcut="⌘0">
        <Maximize2 size={13} strokeWidth={1.75} />
      </PillButton>
    </div>
  );
}

interface PillButtonProps {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  shortcut?: string;
  children: React.ReactNode;
}

function PillButton({ onClick, disabled, label, shortcut, children }: PillButtonProps) {
  const title = shortcut ? `${label}  ${shortcut}` : label;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title}
      className={[
        'w-7 h-7 rounded-1 inline-flex items-center justify-center',
        'text-fg-secondary',
        'focus-visible:outline-none focus-visible:shadow-focus',
        'transition-colors duration-micro ease-out',
        disabled
          ? 'opacity-40 cursor-not-allowed'
          : 'hover:bg-bg-hover hover:text-fg-primary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
